import { useCallback, useEffect, useState } from 'react';

import type {
  AnomaliesResponse,
  CleanedPreviewResponse,
  DiffResponse,
  ProvenanceResponse,
} from '../services/types';
import { api } from '../services/api';
import { AnomaliesTable } from './AnomaliesTable';
import { ConfirmDialog } from './ConfirmDialog';
import { DiffTable } from './DiffTable';
import { ProvenanceSummary } from './ProvenanceSummary';
import { Spinner } from './Spinner';
import { TablePreview } from './TablePreview';

type SubTab = 'preview' | 'diff' | 'provenance' | 'anomalies';

interface OutputPanelProps {
  runId: string;
  runStatus?: string;
  piiColumns?: string[];
}

export function OutputPanel({ runId, runStatus, piiColumns = [] }: OutputPanelProps) {
  const [subTab, setSubTab] = useState<SubTab>('preview');

  // Approval gate state
  const [isApproved, setIsApproved] = useState(false);
  const [approvingDialog, setApprovingDialog] = useState(false);
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState<string>();
  const [dialogPiiColumns, setDialogPiiColumns] = useState<string[]>([]);

  // Download state
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string>();

  // Sub-tab data
  const [preview, setPreview] = useState<CleanedPreviewResponse>();
  const [diff, setDiff] = useState<DiffResponse>();
  const [provenance, setProvenance] = useState<ProvenanceResponse>();
  const [anomalies, setAnomalies] = useState<AnomaliesResponse>();
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingDiff, setLoadingDiff] = useState(false);
  const [loadingProv, setLoadingProv] = useState(false);
  const [loadingAnom, setLoadingAnom] = useState(false);
  const [errorPreview, setErrorPreview] = useState<string>();
  const [errorDiff, setErrorDiff] = useState<string>();
  const [errorProv, setErrorProv] = useState<string>();
  const [errorAnom, setErrorAnom] = useState<string>();

  const [previewOffset, setPreviewOffset] = useState(0);
  const [diffOffset, setDiffOffset] = useState(0);

  const isComplete = runStatus === 'complete';

  // Check approval status on mount.
  useEffect(() => {
    if (!isComplete) return;
    api
      .getRunAudit(runId, 'output_approved')
      .then((events) => setIsApproved(events.length > 0))
      .catch(() => {});
  }, [runId, isComplete]);

  // Fetch preview when tab opens or offset changes.
  const fetchPreview = useCallback(
    (offset: number) => {
      setLoadingPreview(true);
      setErrorPreview(undefined);
      api
        .getCleaned(runId, { limit: 100, offset })
        .then((r) => {
          setPreview(r);
          setPreviewOffset(offset);
        })
        .catch((e: Error) => setErrorPreview(e.message))
        .finally(() => setLoadingPreview(false));
    },
    [runId],
  );

  useEffect(() => {
    if (subTab === 'preview' && !preview && isComplete) fetchPreview(0);
  }, [subTab, preview, isComplete, fetchPreview]);

  useEffect(() => {
    if (subTab === 'diff' && !diff && isComplete) {
      setLoadingDiff(true);
      setErrorDiff(undefined);
      api
        .getDiff(runId, { limit: 500 })
        .then(setDiff)
        .catch((e: Error) => setErrorDiff(e.message))
        .finally(() => setLoadingDiff(false));
    }
  }, [subTab, diff, isComplete, runId]);

  useEffect(() => {
    if (subTab === 'provenance' && !provenance && isComplete) {
      setLoadingProv(true);
      setErrorProv(undefined);
      api
        .getProvenance(runId)
        .then(setProvenance)
        .catch((e: Error) => setErrorProv(e.message))
        .finally(() => setLoadingProv(false));
    }
  }, [subTab, provenance, isComplete, runId]);

  useEffect(() => {
    if (subTab === 'anomalies' && !anomalies && isComplete) {
      setLoadingAnom(true);
      setErrorAnom(undefined);
      api
        .getAnomalies(runId)
        .then(setAnomalies)
        .catch((e: Error) => setErrorAnom(e.message))
        .finally(() => setLoadingAnom(false));
    }
  }, [subTab, anomalies, isComplete, runId]);

  // Approval flow
  const handleApproveClick = async () => {
    // Prefetch PII columns before opening dialog (use cached preview or artifact).
    const cols =
      piiColumns.length > 0
        ? piiColumns
        : (preview?.pii_columns ?? []);
    setDialogPiiColumns(cols);
    setApprovingDialog(true);
  };

  const handleApproveConfirm = async () => {
    setApproving(true);
    setApproveError(undefined);
    try {
      const res = await api.approveOutput(runId);
      setIsApproved(res.approved);
      setDialogPiiColumns(res.pii_columns);
      setApprovingDialog(false);
    } catch (e: unknown) {
      setApproveError(e instanceof Error ? e.message : 'Approval failed');
    } finally {
      setApproving(false);
    }
  };

  // Download flow
  const handleDownload = async () => {
    setDownloading(true);
    setDownloadError(undefined);
    try {
      const { token } = await api.requestDownload(runId);
      // Construct the streaming download URL and open in the same window.
      const base = import.meta.env.VITE_API_BASE_URL ?? '/api';
      const url = `${base}/runs/${encodeURIComponent(runId)}/download/${encodeURIComponent(token)}`;
      const a = document.createElement('a');
      a.href = url;
      a.click();
    } catch (e: unknown) {
      setDownloadError(e instanceof Error ? e.message : 'Download request failed');
    } finally {
      setDownloading(false);
    }
  };

  const handleAck = async (anomalyId: string) => {
    await api.acknowledgeAnomaly(runId, anomalyId);
    // Refresh anomaly list so acknowledged flag updates.
    const refreshed = await api.getAnomalies(runId);
    setAnomalies(refreshed);
  };

  if (!isComplete) {
    return (
      <div
        role="status"
        className="rounded-md border border-border bg-muted/10 p-5 text-sm text-muted"
      >
        Output is available once the pipeline completes successfully. Current
        status: <strong>{runStatus ?? 'unknown'}</strong>.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Approval / download toolbar */}
      <div className="flex items-center gap-3 flex-wrap">
        {!isApproved ? (
          <button
            type="button"
            onClick={() => void handleApproveClick()}
            className="px-4 py-2 text-sm rounded-md bg-brand text-white hover:bg-brand-dark"
          >
            Approve Output
          </button>
        ) : (
          <span className="text-xs text-brand-dark font-medium px-2 py-1 bg-brand/10 rounded-sm">
            ✓ Output approved
          </span>
        )}

        <button
          type="button"
          onClick={() => void handleDownload()}
          disabled={!isApproved || downloading}
          title={!isApproved ? 'Approve the output first' : undefined}
          className="px-4 py-2 text-sm rounded-md border border-border text-ink hover:bg-surface-alt disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {downloading ? 'Preparing…' : 'Download cleaned CSV ⇩'}
        </button>

        {approveError && <p className="text-xs text-danger">{approveError}</p>}
        {downloadError && <p className="text-xs text-danger">{downloadError}</p>}
      </div>

      {/* Sub-tab navigation */}
      <nav
        aria-label="Output views"
        className="flex gap-1 border-b border-border text-sm"
      >
        {(['preview', 'diff', 'provenance', 'anomalies'] as SubTab[]).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setSubTab(t)}
            className={`px-4 py-2 font-medium border-b-2 -mb-px transition capitalize ${
              subTab === t
                ? 'border-brand-dark text-ink'
                : 'border-transparent text-muted hover:text-ink'
            }`}
            aria-current={subTab === t ? 'true' : undefined}
          >
            {t}
          </button>
        ))}
      </nav>

      {/* Sub-tab panels */}
      {subTab === 'preview' && (
        <SubTabPanel loading={loadingPreview} error={errorPreview}>
          {preview && (
            <div className="space-y-3">
              <TablePreview
                headers={preview.rows.length > 0 ? Object.keys(preview.rows[0]) : []}
                rows={preview.rows as Record<string, string | number | null>[]}
                highlight={{ pii: preview.pii_columns }}
                // v0.6 Stage A.3 — gate the reveal on a successful audit
                // POST. If the audit fails (network blip, transient DB
                // outage), the reveal is blocked and the reviewer can
                // retry. We never let PII surface without a paired audit
                // row. Surface label distinguishes Output-tab reveals
                // from Schema/FIA-tab reveals in the run audit timeline.
                onRequestReveal={async () => {
                  const ok = window.confirm(
                    'Reveal PII values in this preview? This action will be ' +
                      'recorded in the run audit log.',
                  );
                  if (!ok) return false;
                  try {
                    await api.recordPiiReveal(
                      runId,
                      'preview',
                      preview.pii_columns,
                    );
                    return true;
                  } catch (err) {
                    console.error('PII reveal audit failed; reveal blocked', err);
                    return false;
                  }
                }}
              />
              <div className="flex items-center justify-between text-xs text-muted">
                <span>
                  Showing {previewOffset + 1}–{previewOffset + preview.rows.length} of{' '}
                  {preview.total_rows} rows
                </span>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => fetchPreview(Math.max(0, previewOffset - 100))}
                    disabled={previewOffset === 0}
                    className="text-brand-dark hover:underline disabled:text-muted disabled:no-underline"
                  >
                    ← Prev
                  </button>
                  <button
                    type="button"
                    onClick={() => fetchPreview(previewOffset + 100)}
                    disabled={previewOffset + preview.rows.length >= preview.total_rows}
                    className="text-brand-dark hover:underline disabled:text-muted disabled:no-underline"
                  >
                    Next →
                  </button>
                </div>
              </div>
            </div>
          )}
        </SubTabPanel>
      )}

      {subTab === 'diff' && (
        <SubTabPanel loading={loadingDiff} error={errorDiff}>
          {diff && (
            <DiffTable
              diffs={diff.diffs}
              totalDiffs={diff.total_diffs}
              offset={diffOffset}
              onPageChange={(next) => {
                setDiffOffset(next);
                setLoadingDiff(true);
                api
                  .getDiff(runId, { limit: 500, offset: next })
                  .then(setDiff)
                  .catch((e: Error) => setErrorDiff(e.message))
                  .finally(() => setLoadingDiff(false));
              }}
            />
          )}
        </SubTabPanel>
      )}

      {subTab === 'provenance' && (
        <SubTabPanel loading={loadingProv} error={errorProv}>
          {provenance && <ProvenanceSummary provenance={provenance} />}
        </SubTabPanel>
      )}

      {subTab === 'anomalies' && (
        <SubTabPanel loading={loadingAnom} error={errorAnom}>
          {anomalies && (
            <AnomaliesTable anomalies={anomalies.anomalies} onAck={handleAck} />
          )}
        </SubTabPanel>
      )}

      {/* Approval dialog */}
      <ConfirmDialog
        isOpen={approvingDialog}
        title="Approve Output for Download"
        confirmLabel="Approve"
        onConfirm={() => void handleApproveConfirm()}
        onCancel={() => setApprovingDialog(false)}
        confirming={approving}
      >
        <p>
          Approving this output confirms the cleansed data is ready for download.
        </p>
        {dialogPiiColumns.length > 0 && (
          <p className="mt-2">
            This output contains{' '}
            <strong>masked PII columns</strong>:{' '}
            <span className="font-mono text-xs">
              {dialogPiiColumns.join(', ')}
            </span>
            . Approving confirms you accept responsibility for how this data is
            handled after download.
          </p>
        )}
      </ConfirmDialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Local helper
// ---------------------------------------------------------------------------

function SubTabPanel({
  loading,
  error,
  children,
}: {
  loading: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  if (loading) {
    return (
      <div className="flex items-center gap-3 text-muted py-4">
        <Spinner /> Loading…
      </div>
    );
  }
  if (error) {
    return <p className="text-sm text-danger py-4">{error}</p>;
  }
  return <>{children}</>;
}
